import React, { useRef } from 'react';
import { gsap } from 'gsap';
import { prefersReducedMotion, useIsoLayoutEffect } from './motion';

/* Progress ring (0–100). The arc draws itself on mount and eases between
   values on change. `value == null` renders an empty dashed track — "no
   data yet" — instead of a misleading 0. */
export default function MasteryRing({
  value,
  color,
  size = 48,
  stroke = 5,
  delay = 0,
  label,
  showValue = true,
  trackColor = 'currentColor',
  children,
}) {
  const arcRef = useRef(null);
  const shownRef = useRef(0);
  const has = value !== null && value !== undefined && !Number.isNaN(value);
  const pct = has ? Math.max(0, Math.min(100, Number(value))) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  useIsoLayoutEffect(() => {
    const arc = arcRef.current;
    if (!arc) return undefined;
    const to = c * (1 - pct / 100);
    if (prefersReducedMotion()) {
      arc.style.strokeDashoffset = `${to}`;
      shownRef.current = pct;
      return undefined;
    }
    const from = c * (1 - shownRef.current / 100);
    arc.style.strokeDashoffset = `${from}`;
    const tween = gsap.to(arc, {
      strokeDashoffset: to,
      duration: 1.1,
      delay,
      ease: 'power3.out',
    });
    return () => {
      // Continue from the drawn position if the value changes mid-draw
      const drawn = parseFloat(arc.style.strokeDashoffset);
      shownRef.current = Number.isFinite(drawn)
        ? Math.round((1 - drawn / c) * 100)
        : pct;
      tween.kill();
    };
  }, [pct, c]);

  return (
    <span
      role="img"
      aria-label={label || (has ? `${pct}%` : '—')}
      style={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeOpacity={has ? 0.12 : 0.28}
          strokeWidth={stroke}
          strokeDasharray={has ? undefined : '3 4'}
        />
        {has && (
          <circle
            ref={arcRef}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            style={{ strokeDashoffset: c * (1 - pct / 100) }}
          />
        )}
      </svg>
      {children ||
        (showValue && (
          <span
            aria-hidden="true"
            style={{
              position: 'relative',
              fontWeight: 800,
              fontSize: Math.max(11, Math.round(size * 0.27)),
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              opacity: has ? 1 : 0.55,
            }}
          >
            {has ? pct : '—'}
            {has && (
              <span style={{ fontSize: '0.62em', fontWeight: 700 }}>%</span>
            )}
          </span>
        ))}
    </span>
  );
}
